import { GalleryImage, Image, ImgFormatLists } from '@zoopla/gallery';
import { SubImage } from '../ListingCard.types';
import { PlaceholderImage } from '@zoopla/assets';

type ResponsiveImage = {
    width?: number | null;
    src?: string | null;
};

type ListingImage =
    | {
          src?: string | null;
          caption?: string | null;
          responsiveImgList?: (ResponsiveImage | null)[] | null;
      }
    | undefined;

const convertSubImageResponseToGalleryImage = (
    image: SubImage
): GalleryImage => {
    return {
        src: image.large ?? '',
        alt: image.caption ?? '',
        responsiveImgList: {
            original: [
                {
                    width: 480,
                    src: image.small ?? ''
                },
                {
                    width: 960,
                    src: image.large ?? ''
                }
            ]
        }
    };
};

const filterEmptyResponsiveImages = (
    responsiveImages?: (ResponsiveImage | null)[]
): ImgFormatLists | null => {
    if (!responsiveImages || responsiveImages.length < 2) {
        return null;
    }

    const [first, second] = responsiveImages;

    const firstToImage: Image = {
        src: first?.src ?? PlaceholderImage,
        width: first?.width ?? 0
    };
    const secondToImage: Image = {
        src: second?.src ?? PlaceholderImage,
        width: second?.width ?? 0
    };

    return { original: [firstToImage, secondToImage] };
};

const convertImagePropsToGalleryImage = (
    image: ListingImage | null
): GalleryImage => {
    if (image) {
        const responsiveImgList = filterEmptyResponsiveImages(
            image?.responsiveImgList ?? []
        );

        if (image.src && responsiveImgList)
            return {
                src: image.src,
                alt: image.caption ?? '',
                responsiveImgList: responsiveImgList
            };
    }

    return {
        src: PlaceholderImage,
        alt: '',
        responsiveImgList: {
            original: [
                { src: PlaceholderImage, width: 480 },
                { src: PlaceholderImage, width: 960 }
            ]
        }
    };
};

const convertImagePropsResponseToSubImage = (image: ListingImage | null) => {
    if (!image)
        return {
            small: PlaceholderImage,
            large: PlaceholderImage,
            caption: ''
        };

    const smallImage = image?.responsiveImgList?.find(
        (responsiveImage) =>
            ((responsiveImage?.width ?? 0) < 960 && responsiveImage?.src) ?? ''
    );

    const definedImageOrPlaceholder = smallImage
        ? smallImage.src
        : PlaceholderImage;

    return {
        small: definedImageOrPlaceholder ?? '',
        large: definedImageOrPlaceholder ?? '',
        caption: image.caption ?? ''
    };
};

const mapSubImageResponseToGalleryImages = (
    otherPropertyImages: SubImage[]
) => {
    if (!otherPropertyImages) return [];

    return otherPropertyImages.map((image) =>
        convertSubImageResponseToGalleryImage(image)
    );
};

export const mapPropertyImageResponseToGalleryImages = (
    image: ListingImage | null,
    otherPropertyImages: (SubImage | null)[]
): GalleryImage[] => {
    const subImages = otherPropertyImages.reduce<SubImage[]>((imgs, img) => {
        if (img === null) {
            return imgs;
        }

        return [
            ...imgs,
            {
                small: img.small ?? '',
                large: img.large ?? '',
                caption: img.caption ?? ''
            }
        ];
    }, []);

    const otherImages = mapSubImageResponseToGalleryImages(subImages);

    const firstImage = convertImagePropsToGalleryImage(image);

    return [firstImage, ...otherImages];
};

export const mapPropertyImageResponseToSrc = (
    image: ListingImage | null,
    otherPropertyImages: (SubImage | null)[]
): SubImage[] => {
    const subImages = otherPropertyImages.reduce<SubImage[]>((imgs, img) => {
        if (img === null) {
            return imgs;
        }

        return [
            ...imgs,
            {
                small: img.small ?? '',
                large: img.large ?? '',
                caption: img.caption ?? ''
            }
        ];
    }, []);

    const firstImage = convertImagePropsResponseToSubImage(image);

    return [firstImage, ...subImages];
};
